import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import LeadDetailSheet from "@/components/LeadDetailSheet";
import { Loader2, Users2 } from "lucide-react";

const STATUS_LABEL = { new: "Nuevo", contacted: "Contactado", qualified: "Calificado", won: "Ganado", lost: "Perdido" };
const STATUS_CLS = {
  new: "bg-[#0055FF]/10 text-[#0055FF]", contacted: "bg-[#F59E0B]/10 text-[#F59E0B]",
  qualified: "bg-[#8B5CF6]/10 text-[#8B5CF6]", won: "bg-[#10B981]/10 text-[#10B981]",
  lost: "bg-[#71717A]/10 text-[#71717A]",
};

export default function LeadsPage() {
  const { activeTenantId } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/leads", { params: statusFilter !== "all" ? { status: statusFilter } : {} });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { if (activeTenantId) load(); }, [activeTenantId, load]);

  const openDetail = (lead) => { setSelected(lead); setSheetOpen(true); };
  const onChanged = (updated) => {
    setSelected(updated);
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A] max-w-xl">
          Leads capturados desde formularios públicos, con atribución completa a la publicación,
          plataforma y vendedor de origen, y asignación automática por territorio.
        </p>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="leads-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-sm border-[#E4E4E7] shadow-none">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <Users2 className="h-8 w-8 text-[#A1A1AA]" />
            <p className="mt-3 text-sm text-[#71717A]">Todavía no llegó ningún lead.</p>
          </div>
        ) : (
          <Table data-testid="leads-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Nombre</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Zip</TableHead>
                <TableHead>Plataforma</TableHead>
                <TableHead>Asignado</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Creado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((l) => (
                <TableRow key={l.id} className="cursor-pointer" onClick={() => openDetail(l)} data-testid={`lead-row-${l.id}`}>
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell className="text-[#71717A]">{l.phone}</TableCell>
                  <TableCell className="text-[#71717A]">{l.zip_code || "-"}</TableCell>
                  <TableCell className="text-[#71717A] capitalize">{l.attribution?.platform || "directo"}</TableCell>
                  <TableCell>
                    {l.assigned_user_id ? (
                      <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 text-[10px] rounded-sm" data-testid={`lead-assigned-${l.id}`}>Asignado</Badge>
                    ) : (
                      <Badge className="bg-[#F59E0B]/10 text-[#92400E] border-0 text-[10px] rounded-sm" data-testid={`lead-unassigned-${l.id}`}>Sin asignar</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${STATUS_CLS[l.status]}`}>
                      {STATUS_LABEL[l.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[#71717A]">{new Date(l.created_at).toLocaleDateString("es-AR")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <LeadDetailSheet lead={selected} open={sheetOpen} onOpenChange={setSheetOpen} onChanged={onChanged} />
    </div>
  );
}
